import { createElement, memo, useEffect, useMemo, useRef, useState } from '@max/max';
import View from '@hfe/max-view';
import Text from '@hfe/max-text';
import ScrollView from '@hfe/max-scrollview';
import TopView from '@max/leez-top-view';
import ModalBaseContainer from '@max/leez-modal-base-container';
import LoadingView from '@max/leez-loading-view';
import RecyclerView from '@hfe/max-recyclerview';
import Button from '@hfe/max-button';
import LinearGradient from '@hfe/max-linear-gradient';
import LImage from '@max/leez-image';
import { useMonitorJumper } from '@nibfe/mcp-monitor-sdk';
import { useRewards } from '../../hooks/useRewards';
import { useCallRecords } from '../../hooks/useCallRecords';
import { RequestStatus } from '../../hooks/useRequest';
import ActivityInvalid from '../activity-invalid';
import { RewardType, PrizeStatus, BizLineDesc } from '../../types/rewardRecords';
import { RecordTabType } from '../../types/shared';
import { parseFreeOrderCardExtra, buildTaskMap, resolveAssistRecordTitle, resolveAssistRecordProgress } from '../../utils/rewardRecords';
import { string2PositiveInt } from '../../utils/number';
import './index.scss';
import { getPriceFontFamily, getPriceWrapper } from '../../utils/style';
import { fenToYuan } from '../../utils/price';
const TAB_CONFIG = [{
        title: '优惠奖励',
        type: RecordTabType.REWARD
    }, {
        title: '进度记录',
        type: RecordTabType.ASSIST
    }];
const priceFontFamily = getPriceFontFamily();
// R00x：奖励进度弹窗默认头图（运营可在 rewardCenterConfig.headerImage 覆盖）
const DEFAULT_HEADER_IMAGE = 'https://img.meituan.net/makerjiulv/cf40f3981e25328d429eea8f5866093d43594.png';
// R00x：弹窗默认底色（设计稿 #eff7ff，运营未配置渐变时使用，与任务弹窗一致）
const DEFAULT_BG_COLOR = '#eff7ff';
const CLOSE_ICON = 'https://p0.meituan.net/ingee/62c376413d9d4422206d04f2c916c280721.png';
function RewardRecords(props) {
    const { feConfig } = props;
    // R00x：奖励进度弹窗头图 / 背景渐变（对齐任务弹窗 taskCenterConfig 取值模式）
    const rewardCfg = feConfig?.rewardCenterConfig;
    const headerImage = rewardCfg?.headerImage || DEFAULT_HEADER_IMAGE;
    const bgStartColor = rewardCfg?.bgStartColor || DEFAULT_BG_COLOR;
    const bgEndColor = rewardCfg?.bgEndColor || DEFAULT_BG_COLOR;
    const { monitorNavigateToSmart } = useMonitorJumper();
    // 助力记录按 taskId 匹配任务（类型/名称）的映射，taskInfos 变化时重建
    const taskMap = useMemo(() => buildTaskMap(props.taskInfos), [props.taskInfos]);
    const [tabIndex, setTabIndex] = useState(props.initialTab ?? RecordTabType.REWARD);
    // 优惠券 tab 状态
    const [couponActiveIndex, setCouponActiveIndex] = useState(0);
    const couponScrollViewRef = useRef(null);
    const { rewardsData, requestStatus: rewardsStatus, loadRewardsData, allCouponData, displayedCouponList, showCouponTabList } = useRewards({
        activityId: props.activityId,
        couponActiveIndex,
        feConfig
    });
    const { allRecords, requestStatus: callRecordsStatus, isFirstLoading: callRecordsFirstLoading, loadCallRecordsData, loadMoreCallRecords, isLoadingMore, hasNextPage } = useCallRecords({
        activityId: props.activityId
    });
    // initialTab 变化时同步更新内部 tab（每次弹窗重新打开时重置）
    useEffect(() => {
        if (props.visible) {
            setTabIndex(props.initialTab);
        }
    }, [props.visible, props.initialTab]);
    // 按需请求：visible 且对应 tab 激活时才发起请求
    useEffect(() => {
        if (!props.visible)
            return;
        tabIndex === RecordTabType.ASSIST ? loadCallRecordsData() : loadRewardsData();
    }, [props.visible, tabIndex]);
    const timestampToDate = (timestamp) => {
        if (typeof timestamp !== 'number' || !isFinite(timestamp)) {
            return '';
        }
        if (timestamp < 0 || timestamp > Number.MAX_SAFE_INTEGER) {
            return '';
        }
        const date = new Date(timestamp);
        const Y = date.getFullYear();
        const M = String(date.getMonth() + 1).padStart(2, '0');
        const D = String(date.getDate()).padStart(2, '0');
        return `${Y}/${M}/${D}`;
    };
    const renderCallRecordItem = (item, key, isLast) => {
        const title = resolveAssistRecordTitle(item, taskMap);
        const progress = resolveAssistRecordProgress(item, props.upperLimit);
        return <View key={`${item.id}_${key}`} className="rr-call-record-item">
        <View className="rr-call-record-left">
          <Text className="rr-call-record-title">{title}</Text>
          <Text className="rr-call-record-time">
            {item.assistTime ? timestampToDate(item.assistTime) : ''} 加速成功
          </Text>
        </View>
        <View className="rr-call-record-right">
          <Text className="rr-call-record-amount">+{progress}%</Text>
          <Text className="rr-call-record-desc">心动进度</Text>
        </View>
      </View>;
    };
    const renderFreeCardItem = (item, key, isLast) => {
        const freeOrderCardExtra = parseFreeOrderCardExtra(item);
        if (freeOrderCardExtra && freeOrderCardExtra.bindName) {
            const { bindName, amount } = freeOrderCardExtra;
            // 已兑换：显示酒店名、免单标签、金额
            const amountVal = freeOrderCardExtra.amount ? Number(freeOrderCardExtra.amount) / 100 : null;
            const amountStr = amountVal == null ? '' : amountVal % 1 === 0 ? String(amountVal) : amountVal.toFixed(2);
            return <View key={key} className="rr-free-card-item">
          <View className="rr-hotel-reward-left">
            <Text className="rr-hotel-reward-title" numberOfLines={1}>
              {bindName || ''}
            </Text>
            <View className="rr-hotel-reward-tag-container">
              
            </View>
          </View>

          <LImage source={{
                uri: 'https://img.meituan.net/makerjiulv/ab8c0d9c3614a8b6f5e2929509a97d9f20606.png'
            }} className="rr-hotel-reward-right" resizeMode="cover" style={{
                width: '176rpx',
                height: '144rpx'
            }}>
            <Text className="rr-hotel-reward-amount">
              <Text className="rr-amount-symbol">¥</Text>
              <Text className="rr-amount-value">{amountStr}</Text>
            </Text>
          </LImage>
        </View>;
        }
        else {
            const titleText = item.amount != null ? `${item.amount}元免单卡` : item.prizeName || '免单卡';
            return <View key={key} className="rr-free-card-item">
          <View className="rr-free-card-content">
            <Text className="rr-free-card-title">{titleText}</Text>
          </View>
          <LinearGradient className="rr-free-card-use-btn" colors={['#FFE74D', '#FFDD00']} start={{
                x: 0,
                y: 0
            }} end={{
                x: 1,
                y: 0
            }} onClick={() => {
                item.jumpUrl && monitorNavigateToSmart(item.jumpUrl);
            }}>
            <Text className="rr-free-card-use-btn-text">去使用</Text>
          </LinearGradient>
        </View>;
        }
    };
    const isLoading = rewardsStatus === RequestStatus.LOADING || callRecordsFirstLoading;
    return <TopView>
      <ModalBaseContainer type="slide" visible={props.visible} contentStyle={{
        alignItems: 'center',
        justifyContent: 'center',
        width: '750rpx',
        height: '1200rpx',
        position: 'relative'
    }} showMask onMaskPress={props.onClose}>
        <View className="rr-container">
          
          <LinearGradient className="rr-bg" colors={[bgStartColor, bgEndColor]} start={{
        x: 0,
        y: 0
    }} end={{
        x: 0,
        y: 1
    }}/>
          <View className="rr-header">
            <View className="rr-title-wrap">
              <LImage source={{
        uri: headerImage
    }} style={{
        width: '750rpx',
        height: '108rpx'
    }}/>
            </View>
            <LImage source={{
        uri: CLOSE_ICON
    }} style={{
        width: '36rpx',
        height: '36rpx',
        position: 'absolute',
        right: '36rpx',
        top: '36rpx'
    }} onPress={props.onClose}/>
          </View>

          <View className="rr-tabs">
            <View className="rr-tabs-bg"/>
            {TAB_CONFIG.map(tab => <View key={tab.type} className={tabIndex === tab.type ? 'rr-tab-item rr-tab-item-active' : 'rr-tab-item'} onClick={() => setTabIndex(tab.type)}>
                <Text className={tabIndex === tab.type ? 'rr-tab-text rr-tab-text-active' : 'rr-tab-text'}>
                  {tab.title}
                </Text>
              </View>)}
          </View>
          
          <View className="rr-content">
            {isLoading ? <View className="rr-loading">
                <LoadingView showIcon showContent bgColorType="light" level="medium" theme={{
        medium: {
            spacing: 15,
            textType: 'body4'
        }
    }} animate content="加载中"/>
              </View> : <>
                
                <View x-if={tabIndex === RecordTabType.REWARD} className="rr-reward-panel">
                  
                  {rewardsData.filter(item => item.rewardType === RewardType.FREE_CARD).map((item, idx, arr) => renderFreeCardItem(item, idx, idx === arr.length - 1))}

                  
                  <View className="rr-coupon-tab-container">
                    {showCouponTabList.map((tab, index) => {
        const tabCouponCount = (allCouponData?.[tab.key] ?? []).length;
        const isActiveTab = couponActiveIndex === index;
        return <Button key={tab.key} className="rr-coupon-tab-item" onPress={() => setCouponActiveIndex(index)}>
                          <Text className="rr-coupon-tab-text" style={{
            color: isActiveTab ? '#111111' : '#2E1F00',
            fontSize: isActiveTab ? '28rpx' : '26rpx',
            opacity: isActiveTab ? 1 : 0.7
        }}>
                            {tab.label}
                            {tab.key === 'all' ? <Text className="rr-coupon-tab-text-append">{tabCouponCount}</Text> : null}
                          </Text>
                          <LinearGradient className="rr-coupon-tab-line" colors={['#FF417D', '#FF417D']} style={{
            opacity: isActiveTab ? 1 : 0
        }} start={{
            x: 0,
            y: 0
        }} end={{
            x: 1,
            y: 1
        }}/>
                        </Button>;
    })}
                  </View>

                  
                  <ScrollView className="rr-coupon-scroll-view" showsVerticalScrollIndicator={false} ref={couponScrollViewRef}>
                    {displayedCouponList.length >= 1 ? <View className="rr-coupon-list-content">
                        {displayedCouponList.map((couponItem, idx) => {
        const isInvalid = couponItem.prizeStatus === PrizeStatus.USED || couponItem.prizeStatus === PrizeStatus.EXPIRED;
        const isCanUse = couponItem.prizeStatus === PrizeStatus.NOT_USED || couponItem.prizeStatus === PrizeStatus.CLAIMED || couponItem.prizeStatus === PrizeStatus.CLAIMABLE;
        const amount = fenToYuan(couponItem.amount);
        return <LImage key={idx} className="coupon-usage-item-wrap" source={{
            uri: 'https://img.meituan.net/makerjiulv/7f4da06ecaa68db1365f98614933d7996032.png'
        }} borderRadius={0}>
                              <View className="coupon-usage-item-left" style={{
            opacity: isInvalid ? 0.5 : 1
        }}>
                                <View className="coupon-usage-item-price">
                                  <View className="coupon-usage-amount-wrap">
                                    <Text className="coupon-usage-item-price-symbol" style={getPriceWrapper('-8rpx')}>
                                      ¥
                                    </Text>
                                    <Text className="coupon-usage-item-price-value" style={{
            fontFamily: priceFontFamily
        }}>
                                      {amount}
                                    </Text>
                                  </View>
                                </View>
                                <Text className="coupon-usage-item-condition" numberOfLines={1}>
                                  {couponItem.requireAmount ? `满${string2PositiveInt(couponItem.requireAmount)}可用` : '无门槛'}
                                </Text>
                              </View>
                              <View className="coupon-usage-item-mid" style={{
            opacity: isInvalid ? 0.5 : 1
        }}>
                                <Text className="coupon-usage-item-title" numberOfLines={1}>
                                  {couponItem.prizeName ?? ''}
                                </Text>
                                {couponItem.expireDesc ? <Text className="coupon-usage-item-limit" numberOfLines={1}>
                                    {couponItem.expireDesc}
                                  </Text> : null}
                                {couponItem.bizLine && BizLineDesc[couponItem.bizLine] ? <Text className="coupon-usage-item-limit" numberOfLines={1} style={{
            marginTop: '17rpx'
        }}>
                                    {`美团${BizLineDesc[couponItem.bizLine]}商品可用`}
                                  </Text> : null}
                              </View>
                              <View className="coupon-usage-item-right">
                                {isCanUse ? <Button className="coupon-usage-item-status-btn-inner" onPress={() => couponItem.jumpUrl && monitorNavigateToSmart(couponItem.jumpUrl)}>
                                    
                                    <LinearGradient className="coupon-usage-item-status-gradient" colors={['#FF002A', '#FE2B92']} start={{
            x: 0,
            y: 0
        }} end={{
            x: 1,
            y: 0
        }}/>
                                    <Text className="coupon-usage-item-status-text coupon-usage-item-status-text--use">
                                      去使用
                                    </Text>
                                  </Button> : null}
                                {isInvalid ? <Text className="coupon-usage-item-status-text">
                                    {couponItem.prizeStatus === PrizeStatus.USED ? '已使用' : '已过期'}
                                  </Text> : null}
                              </View>
                            </LImage>;
    })}
                      </View> : <View className="rr-coupon-empty-content">
                        <LImage source={{
        uri: 'https://p0.meituan.net/travelcube/70ebeaaad24595c07aa27e01375d104512206.png'
    }} style={{
        width: '300rpx',
        height: '326rpx'
    }}/>
                        <Text className="rr-coupon-empty-text">暂无奖励记录～</Text>
                      </View>}
                  </ScrollView>
                </View>

                
                <View x-if={tabIndex === RecordTabType.ASSIST} className="rr-assist-panel">
                  
                  {props.rankTime.startTime && props.rankTime.endTime ? <View className="rr-activity-time">
                      <Text className="rr-activity-time-text">
                        {`活动时间：${timestampToDate(props.rankTime.startTime)} - ${timestampToDate(props.rankTime.endTime)}`}
                      </Text>
                    </View> : null}

                  
                  {allRecords.length > 0 ? <RecyclerView onEndReached={loadMoreCallRecords} onEndReachedThreshold={100} showsVerticalScrollIndicator={false}>
                      {allRecords.map((item, idx) => <RecyclerView.Cell key={`${item.id}_${idx}`}>
                          {renderCallRecordItem(item, idx, idx === allRecords.length - 1)}
                        </RecyclerView.Cell>)}
                      {isLoadingMore ? <LoadingView showIcon bgColorType="light" level="medium" theme={{
        medium: {
            spacing: 15,
            textType: 'body4'
        }
    }} style={{
        marginTop: '15rpx',
        marginBottom: '15rpx'
    }} animate content="加载中"/> : !hasNextPage ? <View style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '10rpx',
        marginBottom: '30rpx'
    }}>
                          <Text className="rr-no-more-text">没有更多了～</Text>
                        </View> : null}
                    </RecyclerView> : <View style={{
        marginTop: '0rpx'
    }}>
                      <ActivityInvalid type="empty" text="暂无助力记录，快去邀请好友吧～" color="#555555" style={{
        marginTop: '100rpx'
    }} textStyle={{
        marginTop: '0rpx'
    }}/>
                    </View>}
                </View>
              </>}
          </View>
        </View>
      </ModalBaseContainer>
    </TopView>;
}
export default memo(RewardRecords);
